import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { TabsContent } from "@/components/ui/tabs";
import { ChevronRight, ChevronDown, Plus, Pencil, Trash2, Building2, Users, GraduationCap, Circle } from "lucide-react";
import { toast } from "sonner";
import { PROVINCE_NAMES, getCitiesForProvince } from "@/lib/idProvinces";
import ImageCropUploader from "@/components/site/ImageCropUploader";
import { completenessOf, COMPLETENESS_META } from "@/lib/completeness";
import { getSchoolUrl } from "@/lib/slug";

const fmtIDR = (n) => (typeof n === "number" && n > 0 ? `Rp ${Math.round(n / 1_000_000)} jt` : "—");

/** Inline dialog for creating/editing a Group. */
function GroupDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState({ name: "", description: "", website: "", logo_url: "" });
  useEffect(() => {
    if (open) setForm({
      name: initial?.name || "", description: initial?.description || "",
      website: initial?.website || "", logo_url: initial?.logo_url || "",
    });
  }, [open, initial]);
  const save = async () => {
    if (!form.name.trim()) { toast.error("Nama grup wajib diisi"); return; }
    try {
      if (initial?.group_id) await api.patch(`/admin/groups/${initial.group_id}`, form);
      else await api.post("/admin/groups", form);
      toast.success(initial ? "Grup diperbarui" : "Grup dibuat");
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal simpan"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="group-dialog">
        <DialogHeader><DialogTitle className="font-serif-display">{initial ? "Edit Grup" : "Buat Grup Baru"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs text-[#4B5563]">Nama Grup</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="mis: BINUS Education Foundation" data-testid="group-name" /></div>
          <div><label className="text-xs text-[#4B5563]">Website</label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://binus.ac.id" data-testid="group-website" /></div>
          <ImageCropUploader
            value={form.logo_url || ""}
            onChange={(url) => setForm({ ...form, logo_url: url })}
            aspect={1}
            label="Logo Grup"
            testidPrefix="group-logo-upload"
          />
          <div><label className="text-xs text-[#4B5563]">Deskripsi</label><Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Yayasan pendidikan…" data-testid="group-description" /></div>
          <Button onClick={save} data-testid="group-save" className="rounded-full bg-[#D2673E] hover:bg-[#B85838]">{initial ? "Simpan Perubahan" : "Buat Grup"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Inline dialog for creating/editing a Campus. */
function CampusDialog({ open, onOpenChange, initial, groups, onSaved, presetGroupId = null }) {
  const [form, setForm] = useState({ name: "", city: "", state: "", address_street: "", group_id: null, logo_url: "" });
  useEffect(() => {
    if (open) setForm({
      name: initial?.name || "", city: initial?.city || "", state: initial?.state || "",
      address_street: initial?.address_street || "",
      group_id: initial?.group_id ?? presetGroupId ?? null,
      logo_url: initial?.logo_url || "",
    });
  }, [open, initial, presetGroupId]);
  const save = async () => {
    if (!form.name.trim()) { toast.error("Nama campus wajib diisi"); return; }
    const payload = { ...form, group_id: form.group_id || null };
    try {
      if (initial?.campus_id) await api.patch(`/admin/campuses/${initial.campus_id}`, payload);
      else await api.post("/admin/campuses", payload);
      toast.success(initial ? "Campus diperbarui" : "Campus dibuat");
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal simpan"); }
  };
  const cityOptions = getCitiesForProvince(form.state) || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="campus-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif-display">{initial ? "Edit Campus" : "Buat Campus Baru"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs text-[#4B5563]">Nama Campus</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="mis: BINUS Simprug" data-testid="campus-name" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-[#4B5563]">Provinsi</label>
              <Select value={form.state || "__none__"} onValueChange={(v) => setForm({ ...form, state: v === "__none__" ? "" : v, city: "" })}>
                <SelectTrigger data-testid="campus-state"><SelectValue placeholder="Pilih provinsi" /></SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="__none__">Pilih provinsi…</SelectItem>
                  {PROVINCE_NAMES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#4B5563]">Kota / Kabupaten</label>
              {cityOptions.length > 0 ? (
                <Select value={form.city || "__none__"} onValueChange={(v) => setForm({ ...form, city: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="campus-city"><SelectValue placeholder="Pilih kota/kabupaten" /></SelectTrigger>
                  <SelectContent className="max-h-80">
                    <SelectItem value="__none__">Pilih kota/kabupaten…</SelectItem>
                    {cityOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder={form.state ? "Ketik nama kota/kabupaten" : "Pilih provinsi dulu"}
                  disabled={!form.state} data-testid="campus-city" />
              )}
            </div>
          </div>
          <div><label className="text-xs text-[#4B5563]">Alamat</label><Input value={form.address_street} onChange={(e) => setForm({ ...form, address_street: e.target.value })} placeholder="mis: Jl. Sisingamangaraja No. 62" data-testid="campus-address" /></div>
          <ImageCropUploader
            value={form.logo_url || ""}
            onChange={(url) => setForm({ ...form, logo_url: url })}
            aspect={1}
            label="Logo Campus"
            testidPrefix="campus-logo-upload"
          />
          <div>
            <label className="text-xs text-[#4B5563]">Grup induk (opsional)</label>
            <Select value={form.group_id || "__none__"} onValueChange={(v) => setForm({ ...form, group_id: v === "__none__" ? null : v })}>
              <SelectTrigger data-testid="campus-group-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Tidak ada (campus mandiri) —</SelectItem>
                {groups.map((g) => <SelectItem key={g.group_id} value={g.group_id}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={save} data-testid="campus-save" className="rounded-full bg-[#D2673E] hover:bg-[#B85838]">{initial ? "Simpan Perubahan" : "Buat Campus"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function HierarchyTab({ isAdminOrSuper, onEditSchool, onAddUnit, refreshKey }) {
  const [tree, setTree] = useState({ groups: [], unassigned_campuses: [], unassigned_units: [] });
  const [expanded, setExpanded] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [groupDlg, setGroupDlg] = useState({ open: false, initial: null });
  const [campusDlg, setCampusDlg] = useState({ open: false, initial: null, presetGroupId: null });

  const load = () => {
    setLoading(true);
    api.get("/admin/hierarchy-tree").then((r) => setTree(r.data)).catch(() => toast.error("Gagal memuat tree")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [refreshKey]);

  const toggle = (id) => setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const deleteGroup = async (g) => {
    if (!window.confirm(`Hapus grup "${g.name}"? Campus di bawahnya akan menjadi mandiri (tidak terhapus).`)) return;
    try { await api.delete(`/admin/groups/${g.group_id}`); toast.success("Grup dihapus"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const deleteCampus = async (c) => {
    if (!window.confirm(`Hapus campus "${c.name}"? Unit di bawahnya akan menjadi standalone (tidak terhapus).`)) return;
    try { await api.delete(`/admin/campuses/${c.campus_id}`); toast.success("Campus dihapus"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const renderCampus = (c) => {
    const cid = `cmp-${c.campus_id}`;
    const open = expanded.has(cid);
    return (
      <div key={c.campus_id} className="border-l-2 border-[#EAE7E0] ml-4 pl-4 mt-2" data-testid={`campus-node-${c.campus_id}`}>
        <div className="flex items-center gap-2 py-2">
          <button onClick={() => toggle(cid)} data-testid={`campus-toggle-${c.campus_id}`} className="text-[#4B5563] hover:text-[#D2673E]">
            {c.units?.length ? (open ? <ChevronDown size={16} /> : <ChevronRight size={16} />) : <span className="w-4 inline-block" />}
          </button>
          {c.logo_url ? (
            <img src={c.logo_url} alt={c.name} className="w-6 h-6 rounded object-contain bg-[#F3F1ED]" />
          ) : (
            <Building2 size={14} className="text-[#1E3A8A]" />
          )}
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[#1F2937] text-sm truncate">{c.name}</div>
            <div className="text-[11px] text-[#4B5563]">{c.city || "—"} · {c.unit_count || 0} unit · avg {(c.avg_rating || 0).toFixed(1)} · SPP {fmtIDR(c.spp_min)}–{fmtIDR(c.spp_max)}</div>
          </div>
          {isAdminOrSuper && <>
            <Button size="sm" variant="outline" className="h-7 px-2 rounded-full" onClick={() => onAddUnit && onAddUnit(c)} data-testid={`add-unit-to-${c.campus_id}`} title="Tambah unit sekolah di cabang ini"><Plus size={12} className="mr-1" />Unit</Button>
            <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => setCampusDlg({ open: true, initial: c, presetGroupId: c.group_id })} data-testid={`edit-campus-${c.campus_id}`}><Pencil size={12} /></Button>
            <Button size="sm" variant="outline" className="h-7 px-2 text-[#BE5730]" onClick={() => deleteCampus(c)} data-testid={`delete-campus-${c.campus_id}`}><Trash2 size={12} /></Button>
          </>}
        </div>
        {open && c.units?.length > 0 && (
          <div className="ml-6 border-l border-[#EAE7E0] pl-4 pb-2">
            {c.units.map((u) => <UnitRow key={u.school_id} unit={u} isAdminOrSuper={isAdminOrSuper} onEditSchool={onEditSchool} />)}
          </div>
        )}
      </div>
    );
  };

  const renderGroup = (g) => {
    const gid = `grp-${g.group_id}`;
    const open = expanded.has(gid);
    return (
      <div key={g.group_id} className="rounded-2xl border border-[#EAE7E0] bg-white p-4 mb-3" data-testid={`group-node-${g.group_id}`}>
        <div className="flex items-center gap-2">
          <button onClick={() => toggle(gid)} data-testid={`group-toggle-${g.group_id}`} className="text-[#4B5563] hover:text-[#D2673E]">
            {g.campuses?.length ? (open ? <ChevronDown size={18} /> : <ChevronRight size={18} />) : <span className="w-[18px] inline-block" />}
          </button>
          <Users size={16} className="text-[#D2673E]" />
          <div className="flex-1 min-w-0">
            <div className="font-serif-display text-lg text-[#1F2937] truncate">{g.name}</div>
            <div className="text-xs text-[#4B5563]">{g.campus_count || 0} cabang · {g.unit_count || 0} unit · avg {(g.avg_rating || 0).toFixed(1)} · SPP {fmtIDR(g.spp_min)}–{fmtIDR(g.spp_max)}</div>
          </div>
          {isAdminOrSuper && <>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => setCampusDlg({ open: true, initial: null, presetGroupId: g.group_id })} data-testid={`add-campus-to-${g.group_id}`}><Plus size={12} className="mr-1" /> Campus</Button>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => setGroupDlg({ open: true, initial: g })} data-testid={`edit-group-${g.group_id}`}><Pencil size={12} /></Button>
            <Button size="sm" variant="outline" className="rounded-full text-[#BE5730]" onClick={() => deleteGroup(g)} data-testid={`delete-group-${g.group_id}`}><Trash2 size={12} /></Button>
          </>}
        </div>
        {open && g.campuses?.map(renderCampus)}
      </div>
    );
  };

  return (
    <TabsContent value="hierarchy" data-testid="hierarchy-panel" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif-display text-2xl text-[#1F2937]">Grup &amp; Cabang</h2>
          <div className="mt-1.5 flex items-center gap-3 text-[11px] text-[#4B5563]" data-testid="completeness-legend">
            <span className="uppercase tracking-widest">Kelengkapan unit:</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#16A34A]" /> Lengkap</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#CA8A04]" /> Parsial</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#DC2626]" /> Kritis (NPSN/SPP kosong)</span>
          </div>
        </div>
        {isAdminOrSuper && (
          <div className="flex gap-2">
            <Button onClick={() => setGroupDlg({ open: true, initial: null })} data-testid="new-group-btn" className="rounded-full bg-[#D2673E] hover:bg-[#B85838]"><Plus size={14} className="mr-1" /> Grup baru</Button>
            <Button onClick={() => setCampusDlg({ open: true, initial: null, presetGroupId: null })} data-testid="new-campus-btn" variant="outline" className="rounded-full"><Plus size={14} className="mr-1" /> Campus baru</Button>
          </div>
        )}
      </div>
      {loading ? <div className="text-[#4B5563]">Memuat hierarchy…</div> : (
        <>
          {tree.groups.map(renderGroup)}
          {tree.unassigned_campuses.length > 0 && (
            <div className="mt-6">
              <div className="text-[11px] uppercase tracking-widest text-[#4B5563] font-semibold mb-2">Campus Mandiri (tanpa Grup)</div>
              <div className="rounded-2xl border border-dashed border-[#EAE7E0] bg-white p-2" data-testid="unassigned-campuses">
                {tree.unassigned_campuses.map(renderCampus)}
              </div>
            </div>
          )}
        </>
      )}
      <GroupDialog open={groupDlg.open} initial={groupDlg.initial} onOpenChange={(v) => setGroupDlg({ ...groupDlg, open: v })} onSaved={load} />
      <CampusDialog open={campusDlg.open} initial={campusDlg.initial} groups={tree.groups} presetGroupId={campusDlg.presetGroupId} onOpenChange={(v) => setCampusDlg({ ...campusDlg, open: v })} onSaved={load} />
    </TabsContent>
  );
}

/** Legend + single unit row with completeness indicator and shortcut Edit button. */
function UnitRow({ unit, isAdminOrSuper, onEditSchool }) {
  const { level, missing } = completenessOf(unit);
  const meta = COMPLETENESS_META[level];
  const title = level === "green" ? "Data lengkap" : `Data ${meta.label.toLowerCase()} — belum: ${missing.join(", ")}`;
  return (
    <div data-testid={`unit-leaf-${unit.school_id}`} className="flex items-center gap-2 py-1.5 text-sm text-[#1F2937] group">
      <span
        title={title}
        aria-label={title}
        data-testid={`unit-completeness-${unit.school_id}`}
        data-level={level}
        className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
        style={{ backgroundColor: meta.color, boxShadow: `0 0 0 2px ${meta.bg}` }}
      />
      <GraduationCap size={12} className="text-[#4B5563]" />
      <Link to={getSchoolUrl(unit)} className="truncate flex-1 hover:text-[#D2673E]">{unit.name}</Link>
      <span className="text-[10px] text-[#4B5563]">{(unit.education_levels || []).join(",") || "—"}</span>
      <span className="text-[10px] text-[#4B5563] tabular-nums">{unit.review_count || 0} review</span>
      {isAdminOrSuper && (
        <Button
          size="sm"
          variant="outline"
          className="h-6 px-2 opacity-70 group-hover:opacity-100"
          onClick={(e) => { e.preventDefault(); onEditSchool && onEditSchool(unit.school_id); }}
          data-testid={`edit-unit-${unit.school_id}`}
          title="Edit Informasi Sekolah (buka modal lengkap)"
        >
          <Pencil size={11} />
        </Button>
      )}
    </div>
  );
}
